import { Controller } from "@hotwired/stimulus";

export default class extends Controller {
  static targets = [
    "list", 
    "arrow", 
    "hiddenInput", 
    "itemTitle", 
    "itemDetails", 
    "itemImage", 
    "clearButton"
  ];

  static values = {
    expanded: { type: Boolean, default: false },
    emptyLabel: { type: String, default: "" },
    showClearButton: { type: Boolean, default: false },
    selectedSelectionKey: { type: String, default: "" }
  };

  connect() {
    this.syncSelectedItemClass();
    this.syncClearButtonVisibility();
  }

  toggle(event) {
    if (event?.target?.closest?.("[data-simple-selector-target='clearButton']")) {
      return;
    }
    this.expandedValue = !this.expandedValue;
  }

  expandedValueChanged(isExpanded) {
    if (!this.hasListTarget) return;

    if (isExpanded) {
      this.listTarget.style.transition = "height 0.5s ease-in-out";

      const height = this.listTarget.scrollHeight;
      this.listTarget.style.height = `${height}px`;

      const onEnd = () => {
        this.listTarget.removeEventListener("transitionend", onEnd);
        if (this.expandedValue) {
          this.listTarget.style.height = "auto";
        }
      };
      this.listTarget.addEventListener("transitionend", onEnd);

      if (this.hasArrowTarget) this.arrowTarget.style.transform = "rotate(0deg)";
    } else {
      if (this.listTarget.style.height === "auto") {
        this.listTarget.style.transition = "none";
        this.listTarget.style.height = `${this.listTarget.scrollHeight}px`;
        this.listTarget.offsetHeight;
      }

      this.listTarget.style.transition = "height 0.2s";
      this.listTarget.style.height = "0px";
      if (this.hasArrowTarget) this.arrowTarget.style.transform = "rotate(180deg)";
    }

    this.syncSelectedItemClass();
  }

  clearSelection(event) {
    event.preventDefault();
    event.stopPropagation();
    this.applyEmptySelection();
    this.expandedValue = false;
  }

  applyEmptySelection() {
    const label = this.emptyLabelValue || "";

    this.selectedSelectionKeyValue = "";
    if (this.hasHiddenInputTarget) this.hiddenInputTarget.value = "";
    if (this.hasItemTitleTarget) this.itemTitleTarget.textContent = label;
    this.updateDetailLines([]);
    if (this.hasItemImageTarget) {
      const wrap = this.itemImageTarget.closest(".image-container");
      this.itemImageTarget.removeAttribute("src");
      this.itemImageTarget.classList.add("u-hidden");
      wrap?.classList.add("u-hidden");
    }

    this.syncSelectedItemClass();
    this.syncClearButtonVisibility();

    this.element.dispatchEvent(
      new CustomEvent("itemSelected", {
        detail: {
          id: "",
          dispatchItem: {
            id: "",
            title: label,
            details: [],
            image: ""
          }
        },
        bubbles: true
      })
    );
  }

  syncClearButtonVisibility() {
    if (!this.hasClearButtonTarget || !this.hasHiddenInputTarget) return;
    const hasValue = (this.hiddenInputTarget.value || "").length > 0;
    this.clearButtonTarget.classList.toggle("u-hidden", !hasValue || !this.showClearButtonValue);
  }

  syncSelectedItemClass() {
    const current = (this.hasHiddenInputTarget ? this.hiddenInputTarget.value : "").trim();
    const primaryKey = (this.selectedSelectionKeyValue || "").trim();
    this.element.querySelectorAll(".Selector-item").forEach((btn) => {
      const rowKey = (btn.dataset.itemSelectionKey || btn.dataset.itemId || "").trim();
      const selected =
        primaryKey.length > 0
          ? rowKey === primaryKey
          : rowKey === current && current.length > 0;
      btn.classList.toggle("Selector-item--selected", selected);
    });
  }

  parseItemDetails(raw) {
    if (!raw || !String(raw).trim()) return [];
    try {
      const parsed = JSON.parse(raw);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }

  updateDetailLines(lines) {
    if (!this.hasItemDetailsTarget) return;
    this.itemDetailsTarget.replaceChildren();
    for (const line of lines) {
      const p = document.createElement("p");
      p.className = (line && line.className) || "";
      p.textContent = (line && line.text) || "";
      this.itemDetailsTarget.appendChild(p);
    }
  }

  select(event) {
    const button = event.currentTarget;
    const { itemId, itemTitle, itemSelectionKey } = button.dataset;
    const rawId = itemId ?? "";
    const lines = this.parseItemDetails(button.dataset.itemDetails);

    const rowImg = button.querySelector("img");
    const imageSrc =
      (rowImg && (rowImg.currentSrc || rowImg.getAttribute("src") || rowImg.src)) || "";

    this.selectedSelectionKeyValue = (itemSelectionKey || rawId || "").trim();
    if (this.hasHiddenInputTarget) this.hiddenInputTarget.value = rawId;
    if (this.hasItemTitleTarget) this.itemTitleTarget.textContent = itemTitle;
    this.updateDetailLines(lines);

    if (this.hasItemImageTarget) {
      const wrap = this.itemImageTarget.closest(".image-container");
      if (imageSrc) {
        this.itemImageTarget.src = imageSrc;
        this.itemImageTarget.classList.remove("u-hidden");
        wrap?.classList.remove("u-hidden");
      } else {
        this.itemImageTarget.removeAttribute("src");
        this.itemImageTarget.classList.add("u-hidden");
        wrap?.classList.add("u-hidden");
      }
    }

    this.syncSelectedItemClass();
    this.syncClearButtonVisibility();

    this.expandedValue = false;

    this.element.dispatchEvent(
      new CustomEvent("itemSelected", {
        detail: {
          id: rawId,
          dispatchItem: {
            id: rawId,
            title: itemTitle,
            details: lines,
            image: imageSrc
          }
        },
        bubbles: true
      })
    );
  }
}
